import { memo, useCallback, useState } from 'react';
import { Ban } from 'lucide-react';
import { AddReactionButton, ReactionPill, cn } from '@cyphex/ui';
import type { Message } from '@cyphex/types';
import { useMessagingPolicy } from '@/hooks/useMyPolicy';
import { formatClock } from '../../lib/time';
import type { DeliveryState, MessageExtras, OutboxItem } from '../../types';
import { MessageAuthorName, MessageAvatarGutter } from './MessageAuthor';
import { MessageFlags } from './MessageFlags';
import { PendingSendNotice } from './PendingSendNotice';
import { AttachmentGrid } from '../content/AttachmentGrid';
import { LinkPreviewCard } from '../content/LinkPreviewCard';
import { RichText } from '../content/RichText';
import { VoiceMessage } from '../content/VoiceMessage';
import { MessageActionsBar } from './MessageActionsBar';
import { DeliveryTick, MessageMeta } from './MessageMeta';
import { ForwardBanner, QuotedMessage } from './QuotedMessage';
import { EmojiPicker } from '../composer/EmojiPicker';

export interface MessageBubbleProps {
  message: Message;
  grouped: boolean;
  /** Last message of this sender's run — where the receipt belongs. */
  groupEnd: boolean;
  isOwn: boolean;
  pending: OutboxItem | null;
  readByOthers: boolean;
  /** Papercut affordances — quick reactions, save, remind, permalink. */
  extras: MessageExtras;
  /** True while a permalink jump is flashing this row. */
  highlighted: boolean;
  onReply: (message: Message) => void;
  onForward: (message: Message) => void;
  onEdit: (message: Message) => void;
  onDelete: (message: Message) => void;
  onPin: (message: Message) => void;
  onReact: (message: Message, emoji: string) => void;
  onRetry: (clientMsgId: string) => void;
  onSendAnyway: (clientMsgId: string) => void;
  onDiscard: (clientMsgId: string) => void;
}

function deliveryOf(pending: OutboxItem | null, isOwn: boolean, readByOthers: boolean): DeliveryState | null {
  if (pending) return pending.state;
  if (!isOwn) return null;
  return readByOthers ? 'read' : 'sent';
}

/** One message row. Memoised — a new message never re-renders older bubbles. */
export const MessageBubble = memo(function MessageBubble({
  message,
  grouped,
  groupEnd,
  isOwn,
  pending,
  readByOthers,
  extras,
  highlighted,
  onReply,
  onForward,
  onEdit,
  onDelete,
  onPin,
  onReact,
  onRetry,
  onSendAnyway,
  onDiscard,
}: MessageBubbleProps) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const { allowUrlPreviews } = useMessagingPolicy();
  const sender = message.sender;
  const deleted = message.state === 'deleted';

  const copy = useCallback(() => {
    void navigator.clipboard.writeText(message.bodyText ?? '');
  }, [message.bodyText]);

  return (
    <article
      data-message-id={message.id}
      className={cn(
        'group/message relative flex gap-2 px-4 transition-colors duration-fast hover:bg-subtle',
        // TEAMS DENSITY. A continuation sits almost flush against the message
        // above it — the run has to read as one paragraph — while the first of
        // a run gets the only real breathing room in the thread. The old
        // `pt-3 pb-0.5` on every row plus a 22px header line made a six-message
        // exchange taller than the viewport.
        grouped ? 'py-px' : 'pt-2 pb-px',
        // SENT ON THE RIGHT, RECEIVED ON THE LEFT. Reversing the row keeps ONE
        // DOM order (avatar, content, actions) while flipping the visual side,
        // so the actions bar stays on the outer edge in both directions instead
        // of colliding with the bubble.
        isOwn && 'flex-row-reverse',
        message.isPinned && 'bg-primary-subtle/40',
        // The permalink flash. `duration-slow` so the ring fades out rather
        // than snapping off, which reads as a glitch.
        highlighted && 'bg-primary-subtle ring-2 ring-inset ring-primary duration-slow',
      )}
    >
      {/* The gutter — avatar on the first message of a run, hover time stamp on
          the rest. Both live in `MessageAuthor`, which also owns the profile
          card the avatar opens. */}
      {isOwn ? null : (
        <MessageAvatarGutter sender={sender} grouped={grouped} createdAt={message.createdAt} />
      )}

      {/* `items-*` on BOTH sides, not just the sender's: without it the bubble is
          a block in a stretch column and a two-letter reply renders as a bar
          running three-quarters of the pane. The bubble must hug its text. */}
      <div className={cn('flex min-w-0 flex-1 flex-col', isOwn ? 'items-end' : 'items-start')}>
        {/* The name is redundant on your own messages — the side says who you
            are — so only the clock and delivery tick ride above them. */}
        {/* `leading-4` on the header row and its name: at the body line-height
            (22px) this one line cost more than the gap between two whole
            groups. Teams' header is a 16px strip. */}
        {!grouped ? (
          <div className="mb-px flex items-baseline gap-2">
            {isOwn ? null : <MessageAuthorName sender={sender} />}
            <MessageMeta
              timestamp={formatClock(message.createdAt)}
              edited={message.editCount > 0}
              delivery={deliveryOf(pending, isOwn, readByOthers)}
              className="leading-4"
            />
            <MessageFlags message={message} />
          </div>
        ) : null}

        {message.forwardedFrom ? <ForwardBanner from={message.forwardedFrom} /> : null}
        {message.replyTo ? (
          <QuotedMessage
            preview={message.replyTo}
            authorName={sender?.displayName ?? 'Someone'}
            className="mb-1"
          />
        ) : null}

        {/* THE BUBBLE. Capped at 75% so a long line wraps into a readable column
            instead of running the full width of a wide screen, and so the two
            sides stay visually distinct even when the pane is narrow. */}
        {/* The bar is anchored to this wrapper so it tracks the BUBBLE's edge.
            The wrapper carries the width cap; the bubble keeps the padding and
            the corners. */}
        <div className="relative max-w-[75%]">
        <div
          className={cn(
            'rounded-2xl px-3 py-1.5 text-md',
            isOwn
              ? 'bg-primary-subtle text-foreground rounded-br-sm'
              : 'bg-secondary text-secondary-foreground rounded-bl-sm',
            // A run of messages from the same person reads as one block, so only
            // the first keeps the square corner that points at its author.
            grouped && (isOwn ? 'rounded-br-2xl' : 'rounded-bl-2xl'),
          )}
        >
          {deleted ? (
            <p className="flex items-center gap-1.5 italic text-muted-foreground">
              <Ban size={14} aria-hidden /> This message was deleted
            </p>
          ) : (
            <>
              <RichText doc={message.body} fallback={message.bodyText} />
              {message.voice ? <VoiceMessage clip={message.voice} className="mt-1.5" /> : null}
              <AttachmentGrid attachments={message.attachments} className="mt-1.5" />
              {/* ADMIN MESSAGING POLICY: `allowUrlPreviews`. The unfurl is a
                  client-side render of data already on the message, so this IS
                  the enforcement point — there is no server fetch to stop, and
                  the console field says exactly that rather than implying an
                  outbound request that does not happen. */}
              {allowUrlPreviews
                ? message.previews.map((preview) =>
                    preview.kind === 'link' ? (
                      <LinkPreviewCard key={preview.url} preview={preview} className="mt-1.5" />
                    ) : null,
                  )
                : null}
            </>
          )}
        </div>

        {!deleted ? (
          <MessageActionsBar
            message={message}
            isOwn={isOwn}
            extras={extras}
            onReply={() => onReply(message)}
            onReact={() => setPickerOpen(true)}
            onForward={() => onForward(message)}
            onEdit={() => onEdit(message)}
            onDelete={() => onDelete(message)}
            onPin={() => onPin(message)}
            onCopy={copy}
          />
        ) : null}
        </div>

        {/* A run has one header, on its first message, so a grouped message has
            nowhere to put its tick. Your latest message is the one you care
            about, so the receipt rides the END of the run — which is also where
            Teams shows it. Ungrouped messages already carry it in the header
            above, so this only fills the gap grouping creates. */}
        {isOwn && grouped && groupEnd && !deleted ? (
          <DeliveryTick
            delivery={deliveryOf(pending, isOwn, readByOthers)}
            className="mt-0.5"
          />
        ) : null}

        {message.reactions.length > 0 || pickerOpen ? (
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            {message.reactions.map((reaction) => (
              <ReactionPill
                key={reaction.emoji}
                emoji={reaction.emoji}
                count={reaction.count}
                reacted={reaction.reacted}
                reactors={reaction.users?.map((user) => user.displayName)}
                onToggle={() => onReact(message, reaction.emoji)}
                size="sm"
              />
            ))}
            <EmojiPicker
              open={pickerOpen}
              onOpenChange={setPickerOpen}
              onSelect={(emoji) => {
                onReact(message, emoji);
                setPickerOpen(false);
              }}
              /*
               * THE "+" IS A HOVER AFFORDANCE, NOT PART OF THE MESSAGE.
               *
               * This row renders whenever a message has ANY reaction, and the
               * picker trigger rode along with it — so the moment you reacted,
               * a dashed "add reaction" button was welded under that message
               * for good. Read back, a thread of reacted messages was a column
               * of empty dashed circles competing with the reactions
               * themselves, which are the only thing there anybody meant to
               * say. Teams shows it on hover and nowhere else.
               *
               * Opacity rather than mounting, for two reasons: the row must not
               * reflow under the cursor as it appears, and a keyboard user has
               * to be able to TAB to it — `focus-visible` brings it back for
               * them. It stays lit while its own popover is open, or picking an
               * emoji would make the control vanish under the pointer.
               */
              trigger={
                <AddReactionButton
                  size="sm"
                  onClick={() => setPickerOpen(true)}
                  className={cn(
                    "opacity-0 transition-opacity duration-fast",
                    "group-hover/message:opacity-100 focus-visible:opacity-100",
                    pickerOpen && "opacity-100",
                  )}
                />
              }
            />
          </div>
        ) : null}

        {pending ? (
          <PendingSendNotice
            pending={pending}
            onRetry={onRetry}
            onSendAnyway={onSendAnyway}
            onDiscard={onDiscard}
          />
        ) : null}

      </div>
    </article>
  );
});
